import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { apiRequest, queryClient } from '@/lib/queryClient';
import { formatPercentage } from '@/lib/utils';
import { AdminNavigation } from '@/components/AdminNavigation';
import { 
  Percent, Plus, Trash2, Edit, Users, Hash, DollarSign, 
  Activity, CheckCircle, XCircle, Download, Filter, Settings
} from 'lucide-react';
import { Link } from 'wouter';
import type { 
  RestaurantDiscountCode, 
  InsertRestaurantDiscountCode,
  DiscountTierConfig
} from '@shared/schema';

interface DiscountCodeWithStats extends RestaurantDiscountCode {
  usageCount: number;
  totalDiscountAmount: number;
  discountPercentage: number;
}

interface AddEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  code?: DiscountCodeWithStats;
  tierConfigs?: any[];
}

function AddEditModal({ isOpen, onClose, code, tierConfigs }: AddEditModalProps) {
  const [formData, setFormData] = useState<Partial<InsertRestaurantDiscountCode>>({
    code: code?.code || '',
    restaurantName: code?.restaurantName || '',
    contactPerson: code?.contactPerson || '',
    email: code?.email || '',
    phone: code?.phone || '',
    tier: code?.tier || 'tier1',
    active: code?.active ?? true
  });
  const { toast } = useToast();

  const createMutation = useMutation({
    mutationFn: (data: InsertRestaurantDiscountCode) => 
      apiRequest('POST', '/api/admin/restaurant/discount-codes', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-restaurant-discount-codes'] });
      toast({ title: 'Discount code created successfully' });
      onClose();
    },
    onError: (error: any) => {
      toast({ 
        title: 'Failed to create discount code', 
        description: error.message || 'Unknown error',
        variant: 'destructive' 
      });
    }
  });

  const updateMutation = useMutation({
    mutationFn: (data: Partial<InsertRestaurantDiscountCode>) => 
      apiRequest('PUT', `/api/admin/restaurant/discount-codes/${code?.id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-restaurant-discount-codes'] });
      toast({ title: 'Discount code updated successfully' });
      onClose();
    },
    onError: (error: any) => {
      toast({ 
        title: 'Failed to update discount code', 
        description: error.message || 'Unknown error',
        variant: 'destructive' 
      });
    }
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate 4 character alphanumeric code
    if (!formData.code || !/^[A-Z0-9]{4}$/.test(formData.code)) {
      toast({ 
        title: 'Invalid code', 
        description: 'Code must be exactly 4 alphanumeric characters',
        variant: 'destructive' 
      });
      return;
    }

    if (!formData.restaurantName) {
      toast({ 
        title: 'Restaurant name is required',
        variant: 'destructive' 
      });
      return;
    }

    if (code) {
      updateMutation.mutate(formData);
    } else {
      createMutation.mutate(formData as InsertRestaurantDiscountCode);
    }
  };

  const getTierPercentage = (tier: string) => {
    const config = tierConfigs?.find(c => c.tier === tier);
    if (!config) return '0.00';
    // Round to avoid floating point issues
    const percentage = Math.round(config.discountPercentage * 10000) / 100;
    return percentage.toFixed(2);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{code ? 'Edit' : 'Add'} Restaurant Discount Code</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="code">Discount Code (4 chars)</Label>
              <Input
                id="code"
                maxLength={4}
                pattern="[A-Z0-9]{4}"
                placeholder="ABC1"
                value={formData.code}
                onChange={(e) => {
                  const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
                  setFormData({ ...formData, code: value.slice(0, 4) });
                }}
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="tier">Discount Tier</Label>
              <Select
                value={formData.tier}
                onValueChange={(value: 'tier0' | 'tier1' | 'tier2' | 'tier3') => 
                  setFormData({ ...formData, tier: value })
                }
              >
                <SelectTrigger id="tier">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {tierConfigs?.map(config => (
                    <SelectItem key={config.tier} value={config.tier}>
                      {config.tierName} ({getTierPercentage(config.tier)}% off)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {formData.tier && (
                <p className="text-sm text-gray-600">
                  {getTierPercentage(formData.tier)}% discount on eligible items
                </p>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="restaurantName">Restaurant Name</Label>
            <Input
              id="restaurantName"
              value={formData.restaurantName}
              onChange={(e) => setFormData({ ...formData, restaurantName: e.target.value })}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="contactPerson">Contact Person</Label>
            <Input
              id="contactPerson"
              value={formData.contactPerson}
              onChange={(e) => setFormData({ ...formData, contactPerson: e.target.value })}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">Phone</Label>
              <Input
                id="phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={createMutation.isPending || updateMutation.isPending}
            >
              {code ? 'Update' : 'Create'} Code
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// Enhanced Tier Configuration Modal
function TierConfigModal({ 
  tierConfig, 
  isOpen, 
  onClose 
}: { 
  tierConfig: any | null;
  isOpen: boolean;
  onClose: () => void;
}) {
  const [formData, setFormData] = useState({
    tierName: tierConfig?.tierName || '',
    discountPercentage: Math.round((tierConfig?.discountPercentage || 0) * 10000) / 100,
    rebatePercentage: Math.round((tierConfig?.rebatePercentage || 0) * 10000) / 100,
    rebatePeriod: tierConfig?.rebatePeriod || 'quarterly',
    minPurchaseAmount: tierConfig?.minPurchaseAmount || 0,
    description: tierConfig?.description || ''
  });
  const { toast } = useToast();

  const updateTierMutation = useMutation({
    mutationFn: async (data: any) => {
      const tierConfigId = tierConfig.id;
      return apiRequest('PUT', `/api/admin/discount-tiers/${tierConfigId}`, {
        tierName: data.tierName,
        discountPercentage: data.discountPercentage / 100,
        rebatePercentage: data.rebatePercentage / 100,
        rebatePeriod: data.rebatePeriod,
        minPurchaseAmount: data.minPurchaseAmount,
        description: data.description
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-restaurant-discount-codes'] });
      queryClient.invalidateQueries({ queryKey: ['admin-restaurant-discount-matrix'] });
      queryClient.invalidateQueries({ queryKey: ['admin-discount-tiers'] });
      toast({ title: 'Tier configuration updated successfully' });
      onClose();
    },
    onError: (error: any) => {
      toast({ 
        title: 'Failed to update tier configuration', 
        description: error.message || 'Unknown error',
        variant: 'destructive' 
      });
    }
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (tierConfig) {
      updateTierMutation.mutate(formData);
    }
  };

  if (!tierConfig) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Configure {tierConfig.tier.toUpperCase()} Tier</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="tierName">Tier Display Name</Label>
              <Input
                id="tierName"
                value={formData.tierName}
                onChange={(e) => setFormData({ ...formData, tierName: e.target.value })}
                placeholder="e.g., Bronze, Silver, Gold"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="discountPercentage">Immediate Discount (%)</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="discountPercentage"
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={formData.discountPercentage.toFixed(2)}
                  onChange={(e) => setFormData({ ...formData, discountPercentage: parseFloat(e.target.value) || 0 })}
                  required
                />
                <span className="text-gray-600">%</span>
              </div>
            </div>
          </div>

          <div className="space-y-4 p-4 bg-blue-50 rounded-lg">
            <h3 className="font-semibold text-blue-900">Rebate Configuration</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="rebatePercentage">Additional Rebate (%)</Label>
                <div className="flex items-center gap-2">
                  <Input
                    id="rebatePercentage"
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    value={formData.rebatePercentage === '' ? '' : formData.rebatePercentage.toFixed(2)}
                    onChange={(e) => setFormData({ ...formData, rebatePercentage: e.target.value === '' ? '' : parseFloat(e.target.value) || 0 })}
                  />
                  <span className="text-gray-600">%</span>
                </div>
                <p className="text-xs text-blue-700">Applied retroactively on eligible purchases</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="rebatePeriod">Rebate Period</Label>
                <Select
                  value={formData.rebatePeriod}
                  onValueChange={(value) => setFormData({ ...formData, rebatePeriod: value })}
                >
                  <SelectTrigger id="rebatePeriod">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="quarterly">Quarterly</SelectItem>
                    <SelectItem value="annually">Annually</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="minPurchaseAmount">Minimum Purchase for Rebate Eligibility</Label>
              <div className="flex items-center gap-2">
                <span className="text-gray-600">$</span>
                <Input
                  id="minPurchaseAmount"
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.minPurchaseAmount === '' ? '' : formData.minPurchaseAmount.toFixed(2)}
                  onChange={(e) => setFormData({ ...formData, minPurchaseAmount: e.target.value === '' ? '' : parseFloat(e.target.value) || 0 })}
                />
              </div>
              <p className="text-xs text-blue-700">Customer must meet this amount in the period to qualify for rebate</p>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Tier Description</Label>
            <textarea
              id="description"
              className="w-full p-2 border rounded-md"
              rows={3}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Description of tier benefits and eligibility"
            />
          </div>

          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="font-medium mb-2">Effective Benefits Summary:</h4>
            <ul className="text-sm space-y-1">
              <li>• <strong>{formData.discountPercentage.toFixed(2)}%</strong> immediate discount on all eligible products</li>
              <li>• Additional <strong>{formData.rebatePercentage.toFixed(2)}%</strong> rebate applied {formData.rebatePeriod}</li>
              <li>• Total potential savings: <strong>{(formData.discountPercentage + formData.rebatePercentage).toFixed(2)}%</strong></li>
              {formData.minPurchaseAmount > 0 && (
                <li>• Rebate requires minimum ${formData.minPurchaseAmount.toFixed(2)} purchase per {formData.rebatePeriod.replace('ly', '')} period</li>
              )}
            </ul>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={updateTierMutation.isPending}>
              {updateTierMutation.isPending ? 'Updating...' : 'Update Configuration'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminRestaurantDiscountCodes() {
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingCode, setEditingCode] = useState<DiscountCodeWithStats | null>(null);
  const [filterTier, setFilterTier] = useState<string>('all');
  const [editingTierConfig, setEditingTierConfig] = useState<any | null>(null);
  const { toast } = useToast();

  const { data: codes, isLoading } = useQuery({
    queryKey: ['admin-restaurant-discount-codes'],
    queryFn: () => apiRequest<DiscountCodeWithStats[]>('/api/admin/restaurant/discount-codes'),
  });

  const { data: tierConfigs } = useQuery({
    queryKey: ['admin-discount-tiers'],
    queryFn: () => apiRequest<any[]>('/api/admin/discount-tiers'),
  });

  const toggleActiveMutation = useMutation({
    mutationFn: ({ id, active }: { id: number; active: boolean }) => 
      apiRequest('PATCH', `/api/admin/restaurant/discount-codes/${id}`, { active }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-restaurant-discount-codes'] });
      toast({ title: 'Status updated successfully' });
    },
    onError: (error: any) => {
      toast({ 
        title: 'Failed to update status', 
        description: error.message || 'Unknown error',
        variant: 'destructive' 
      });
    }
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => 
      apiRequest('DELETE', `/api/admin/restaurant/discount-codes/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-restaurant-discount-codes'] });
      toast({ title: 'Discount code deleted successfully' });
    },
    onError: (error: any) => {
      toast({ 
        title: 'Failed to delete discount code', 
        description: error.message || 'Unknown error',
        variant: 'destructive' 
      });
    }
  });

  const handleDelete = (code: DiscountCodeWithStats) => {
    if (confirm(`Are you sure you want to delete the discount code for ${code.restaurantName}?`)) {
      deleteMutation.mutate(code.id);
    }
  };

  const exportToCsv = () => {
    if (!codes) return;

    const csvData = codes.map(code => ({
      'Code': code.code,
      'Restaurant': code.restaurantName,
      'Contact Person': code.contactPerson || '',
      'Email': code.email || '',
      'Phone': code.phone || '',
      'Tier': code.tier,
      'Discount %': code.discountPercentage,
      'Active': code.active ? 'Yes' : 'No',
      'Usage Count': code.usageCount,
      'Total Discount Amount': `$${code.totalDiscountAmount.toFixed(2)}`
    }));

    const headers = Object.keys(csvData[0]).join(',');
    const rows = csvData.map(row => Object.values(row).map(v => 
      typeof v === 'string' && v.includes(',') ? `"${v}"` : v
    ).join(','));
    const csv = [headers, ...rows].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `restaurant-discount-codes-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const filteredCodes = codes?.filter(code => 
    filterTier === 'all' || code.tier === filterTier
  );

  const tierStats = codes?.reduce((acc, code) => {
    if (!acc[code.tier]) {
      acc[code.tier] = { count: 0, usage: 0, discountAmount: 0 };
    }
    acc[code.tier].count++;
    acc[code.tier].usage += code.usageCount;
    acc[code.tier].discountAmount += code.totalDiscountAmount;
    return acc;
  }, {} as Record<string, { count: number; usage: number; discountAmount: number }>);

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/3"></div>
          <div className="h-64 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <AdminNavigation />

      <div className="container mx-auto p-4 max-w-7xl space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Restaurant Discount & Rebate System</h1>
          <p className="text-gray-600 mt-1">Manage discount codes, tier configurations, and rebate programs</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={exportToCsv} variant="outline">
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
          <Button onClick={() => setIsAddModalOpen(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Add Code
          </Button>
        </div>
      </div>

      {/* Tabs for Codes and Tier Management */}
      <Tabs defaultValue="codes" className="space-y-4">
        <TabsList className="grid w-full grid-cols-2 max-w-md bg-gray-100 p-1 rounded-lg">
          <TabsTrigger value="codes" className="font-semibold text-base data-[state=active]:bg-white data-[state=active]:shadow-sm">
            <span className="text-lg font-bold mr-2">1.</span>
            Discount Codes
          </TabsTrigger>
          <TabsTrigger value="tiers" className="font-semibold text-base data-[state=active]:bg-white data-[state=active]:shadow-sm">
            <span className="text-lg font-bold mr-2">2.</span>
            Tier Configuration
          </TabsTrigger>
        </TabsList>

        <TabsContent value="codes" className="space-y-6">
          {/* Filter */}
          <div className="flex items-center gap-4">
            <Filter className="w-5 h-5 text-gray-500" />
            <Select value={filterTier} onValueChange={setFilterTier}>
              <SelectTrigger className="w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Tiers</SelectItem>
                {tierConfigs?.map(config => (
                  <SelectItem key={config.tier} value={config.tier}>
                    {config.tierName} ({(Math.round(config.discountPercentage * 10000) / 100).toFixed(2)}%)
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Codes Table */}
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Code</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Restaurant</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Contact</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Tier</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Usage</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Status</th>
                      <th className="px-4 py-3 text-right text-sm font-medium text-gray-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {filteredCodes?.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="px-4 py-8 text-center text-gray-500">
                          No discount codes found matching your criteria
                        </td>
                      </tr>
                    ) : (
                      filteredCodes?.map((code) => (
                      <tr key={code.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <Hash className="w-4 h-4 text-gray-400" />
                            <span className="font-mono font-bold text-lg">{code.code}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div>
                            <div className="font-medium">{code.restaurantName}</div>
                            {code.contactPerson && (
                              <div className="text-sm text-gray-600">{code.contactPerson}</div>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-sm">
                            {code.email && <div>{code.email}</div>}
                            {code.phone && <div className="text-gray-600">{code.phone}</div>}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <Badge className={
                            code.tier === 'tier0' ? 'bg-gray-100 text-gray-800' :
                            code.tier === 'tier1' ? 'bg-blue-100 text-blue-800' :
                            code.tier === 'tier2' ? 'bg-purple-100 text-purple-800' :
                            'bg-orange-100 text-orange-800'
                          }>
                            {formatPercentage(code.discountPercentage, 2)} {code.tier === 'tier0' ? '' : 'off'}
                          </Badge>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1">
                            <Activity className="w-4 h-4 text-gray-400" />
                            <span>{code.usageCount}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <Switch
                              checked={code.active}
                              onCheckedChange={(checked) => 
                                toggleActiveMutation.mutate({ id: code.id, active: checked })
                              }
                            />
                            <span className={code.active ? "text-green-600 text-sm" : "text-gray-500 text-sm"}>
                              {code.active ? 'Active' : 'Inactive'}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex justify-end gap-2">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setEditingCode(code)}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDelete(code)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              title="Delete discount code"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="tiers" className="space-y-6">
          {/* Tier Configuration Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {tierConfigs?.map(config => {
              const stats = tierStats?.[config.tier] || { count: 0, usage: 0, discountAmount: 0 };
              return (
                <Card key={config.tier} className="bg-gradient-to-br from-white to-gray-50">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center justify-between">
                      {config.tierName}
                      <div className="flex items-center gap-2">
                        <div className="flex flex-col gap-1">
                          <Badge className="bg-emerald-100 text-emerald-800 text-xs">
                            {(Math.round(config.discountPercentage * 10000) / 100).toFixed(2)}% discount
                          </Badge>
                          {config.rebatePercentage > 0 && (
                            <Badge className="bg-blue-100 text-blue-800 text-xs">
                              +{(Math.round(config.rebatePercentage * 10000) / 100).toFixed(2)}% rebate
                            </Badge>
                          )}
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setEditingTierConfig(config)}
                          className="h-7 w-7 p-0"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Active Codes:</span>
                        <span className="font-semibold">{stats.count}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Total Usage:</span>
                        <span className="font-semibold">{stats.usage}</span>
                      </div>
                      <div className="text-xs text-gray-600 mt-2">
                        Rebate: {config.rebatePeriod} | Min: ${config.minPurchaseAmount.toFixed(2)}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>
      </Tabs>

      {/* Add/Edit Modal */}
      <AddEditModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        tierConfigs={tierConfigs}
      />
      {editingCode && (
        <AddEditModal
          isOpen={true}
          onClose={() => setEditingCode(null)}
          code={editingCode}
          tierConfigs={tierConfigs}
        />
      )}
      
      {/* Tier Configuration Modal */}
      {editingTierConfig && (
        <TierConfigModal
          tierConfig={editingTierConfig}
          isOpen={true}
          onClose={() => setEditingTierConfig(null)}
        />
      )}
    </div>
    </div>
  );
}